/**
 * Trial Complete — Upgrade to Annual Modal
 *
 * Trigger: User's trial ended and they were auto-converted to Starter quarterly.
 * Shown once on first login post-trial.
 *
 * Variants:
 *   3A — Used all trial credits
 *   3B — Used some but not all credits
 *   3C — Used no credits (no modal)
 *
 * DEV: Set DEV_FORCE = true to show immediately without condition checks.
 */
import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { useToast } from './ui/toast';
import {
  getSeatInfo,
  switchBillingTerm,
  getEstimateCredits,
} from '../../application/routes/settings/components/PaymentMethods/paymentApi';

// TODO: Set to false before merging
const DEV_FORCE = false;
const DEV_FORCE_VARIANT: '3A' | '3B' = '3A';

const MODAL_KEY_PREFIX = 'paiv_trial_converted_modal_shown_';
const PREV_STATUS_KEY_PREFIX = 'paiv_prev_subscription_status_';

declare const global: { me: { id: string; orgID: string } };

type Variant = '3A' | '3B' | null;

export function TrialConvertedModal() {
  const toast = useToast();
  const orgId: string | undefined = (global as any)?.me?.orgID;

  const [open, setOpen] = useState(false);
  const [variant, setVariant] = useState<Variant>(null);
  const [switching, setSwitching] = useState(false);

  const check = useCallback(async () => {
    if (!orgId) {
      return;
    }

    if (DEV_FORCE) {
      setVariant(DEV_FORCE_VARIANT);
      setOpen(true);
      return;
    }

    const modalKey = `${MODAL_KEY_PREFIX}${orgId}`;
    if (localStorage.getItem(modalKey)) {
      return;
    }

    try {
      const res = await getSeatInfo(orgId);
      if (!res.success || !res.data) {
        return;
      }

      const { subscriptionStatus, billingTerm, customerType } = res.data;

      if (
        customerType !== 'seat_based' ||
        subscriptionStatus !== 'active' ||
        (billingTerm?.toLowerCase() !== 'quarterly' &&
          billingTerm?.toLowerCase() !== 'monthly')
      ) {
        localStorage.setItem(
          `${PREV_STATUS_KEY_PREFIX}${orgId}`,
          subscriptionStatus
        );
        return;
      }

      const prevStatus = localStorage.getItem(
        `${PREV_STATUS_KEY_PREFIX}${orgId}`
      );
      if (prevStatus !== 'trialing') {
        localStorage.setItem(
          `${PREV_STATUS_KEY_PREFIX}${orgId}`,
          subscriptionStatus
        );
        return;
      }

      const creditsRes = await getEstimateCredits(orgId);
      if (!creditsRes.success || !creditsRes.data) {
        return;
      }

      const { granted, used } = creditsRes.data;

      // 3C: no credits used — no modal
      if (used === 0) {
        localStorage.setItem(modalKey, 'true');
        return;
      }

      const v: Variant = used >= granted ? '3A' : '3B';
      setVariant(v);
      setOpen(true);
    } catch {
      // Non-critical
    }
  }, [orgId]);

  useEffect(() => {
    const t = setTimeout(check, 1000);
    return () => clearTimeout(t);
  }, [check]);

  const handleSwitchToAnnual = async () => {
    if (!orgId) {
      return;
    }
    try {
      setSwitching(true);
      const res = await switchBillingTerm(orgId, 'yearly');
      if (res.success) {
        toast.success('Switched to annual billing. Welcome aboard!');
        markShown();
        setOpen(false);
      } else {
        toast.error(res.error || 'Failed to switch to annual');
      }
    } catch {
      toast.error('Unable to switch plans. Please try again.');
    } finally {
      setSwitching(false);
    }
  };

  const handleDismiss = () => {
    markShown();
    setOpen(false);
  };

  const markShown = () => {
    if (!DEV_FORCE && orgId) {
      localStorage.setItem(`${MODAL_KEY_PREFIX}${orgId}`, 'true');
      localStorage.setItem(`${PREV_STATUS_KEY_PREFIX}${orgId}`, 'active');
    }
  };

  if (!variant) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          handleDismiss();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="inline-flex items-center gap-1.5 mb-3">
            <span className="size-2 rounded-full bg-green-500" />
            <span className="text-xs font-semibold text-green-600 dark:text-green-400">
              Trial complete — you&apos;re now on Starter quarterly
            </span>
          </div>

          <DialogTitle className="text-xl font-bold text-foreground">
            {variant === '3A'
              ? 'Save $300/seat by switching to annual'
              : 'Activate annual to keep the momentum going'}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {variant === '3A'
            ? "You've been auto-converted to quarterly billing. Switch to annual today and get 10 free estimates + 10 measurement reports every year — plus a lower monthly rate."
            : 'Your trial is up. Switch to annual and start your full allotment of 10 free estimates and 10 measurement reports — ready to use whenever your next job comes in.'}
        </p>

        {variant === '3A' && (
          <div className="flex gap-4 py-2">
            <div className="flex-1 p-3 rounded-lg bg-muted/50 text-center">
              <p className="text-lg font-bold text-foreground">10</p>
              <p className="text-xs text-muted-foreground">
                free estimates / year
              </p>
            </div>
            <div className="flex-1 p-3 rounded-lg bg-muted/50 text-center">
              <p className="text-lg font-bold text-foreground">10</p>
              <p className="text-xs text-muted-foreground">
                free measurement reports / year
              </p>
            </div>
          </div>
        )}

        {variant === '3A' && (
          <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2">
            At 1 seat, switching saves $300/yr on your plan + up to $1,090 in
            free estimates and reports.
          </p>
        )}

        <Button
          className="w-full"
          onClick={handleSwitchToAnnual}
          disabled={switching}
        >
          {switching ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
          Switch to annual — $50/seat/mo
        </Button>

        <div className="border border-border rounded-lg p-4 space-y-2">
          <p className="text-sm font-semibold text-foreground">
            FieldInspect Pro annual
          </p>
          <p className="text-xs text-muted-foreground">
            15 free estimates + 15 reports per year, plus workflow
            customization, CRM integrations, and priority support.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleDismiss}
          >
            Talk to the FieldInspect team
          </Button>
        </div>

        <p className="text-xs text-muted-foreground text-center">
          You can switch plans at any time from your billing settings.
        </p>
      </DialogContent>
    </Dialog>
  );
}
